import type { FunctionComponent } from 'react'
import React, { useRef, useState } from 'react'

import classnames from 'classnames'

import { AnimationWrapper } from '../../AnimationWrapper'
import { ChevronDownIcon } from '../../Icons/Navigational/ChevronDown'
import { Link } from '../../Link'
import styles from './LinksListExpandable.module.scss'

export const LinksListExpandable: FunctionComponent<
  LinksListExpandableProps
> = ({ button, items = [], className, contentHtmlId }) => {
  const [isActive, setActiveState] = useState<boolean>(false)
  const contentElement = useRef<HTMLDivElement>(null)

  if (!items.length) {
    return null
  }

  return (
    <div
      className={classnames(
        styles.linksList,
        isActive && styles.linksListActive,
        className
      )}
      data-testid="links-list-expandable"
    >
      <button
        className={styles.linksListButton}
        onClick={() => setActiveState(!isActive)}
        aria-expanded={isActive}
        aria-controls={contentHtmlId}
        aria-label={button.ariaLabel}
        type="button"
      >
        <ChevronDownIcon className={styles.linksListToggleIcon} />
        <AnimationWrapper>{button.text}</AnimationWrapper>
      </button>
      <div
        ref={contentElement}
        className={styles.linksListContent}
        aria-hidden={!isActive}
        style={{
          maxHeight: isActive ? contentElement.current?.scrollHeight : '0',
        }}
        id={contentHtmlId}
      >
        <ul className={styles.linksListContentList}>
          {items.map(({ label, href }, index) => (
            <li
              className={styles.linksListContentItem}
              key={`${label}-${index}`}
            >
              <Link href={href} {...(!isActive && { tabIndex: -1 })}>
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type LinksListExpandableProps = {
  /**
   * Array of items with href and label to reveal when the above button is clicked
   */
  items: Array<LinkListItem>
  /**
   * Optional CSS class name to be applied to the component
   */
  className?: string
  /**
   * Text to be assigned to the expandable button, and optional aria-label to be applied
   */
  button: {
    text: string
    ariaLabel?: string
  }
  /**
   * Required unique ID for accessibility to associate list with expand button.
   * Must be unique e.g. using useId()
   */
  contentHtmlId: string
}

export type LinkListItem = {
  href: string
  label: string
}
